import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Alert, Card, Skeleton, alpha } from '@mui/material';
import ChartControls from './ChartControls';
import IndicatorChartCard from './IndicatorChartCard';

function IndicatorChartsView({
  historyData,
  loadingHistory,
  selectedIndicators,
  setSelectedIndicators,
  historyTimeRange,
  setHistoryTimeRange,
  onIndicatorClick,
}) {
  const { t } = useTranslation();
  
  // Render loading skeleton for charts
  const renderChartsSkeleton = () => (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 2,
      '& > *': { 
        flex: '1 1 400px',
        minWidth: 0,
      }
    }}>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card
          key={i}
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: alpha('#667eea', 0.02)
          }}
        >
          {/* Chart Header */}
          <Box sx={{ mb: 2 }}>
            <Skeleton 
              variant="text" 
              width="60%" 
              height={24}
              animation="wave"
              sx={{ mb: 0.5 }}
            />
            <Skeleton 
              variant="text" 
              width="40%" 
              height={20}
              animation="wave"
            />
          </Box>
          
          {/* Chart Area */}
          <Skeleton 
            variant="rounded" 
            width="100%" 
            height={200}
            animation="wave"
            sx={{ borderRadius: 1 }}
          />
          
          {/* Latest Value */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton 
              variant="text" 
              width="30%" 
              height={20}
              animation="wave"
            />
            <Skeleton 
              variant="text" 
              width="25%" 
              height={20}
              animation="wave"
            />
          </Box>
        </Card>
      ))}
    </Box>
  );
  
  return (
    <Box sx={{ mb: 3 }}>
      {/* Controls - Always visible */}
      <ChartControls
        historyData={historyData}
        selectedIndicators={selectedIndicators}
        setSelectedIndicators={setSelectedIndicators}
        historyTimeRange={historyTimeRange}
        setHistoryTimeRange={setHistoryTimeRange}
      />
      
      {/* Charts Section */}
      {loadingHistory ? (
        renderChartsSkeleton()
      ) : selectedIndicators.length === 0 ? (
        <Alert severity="info">
          {t('observations.selectIndicatorsToView', 'Please select indicators above to view their charts')}
        </Alert>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2,
          '& > *': { 
            flex: '1 1 400px',
            minWidth: 0,
          }
        }}>
          {selectedIndicators
            .filter(id => historyData[id])
            .map((indicatorId) => (
              <IndicatorChartCard
                key={indicatorId}
                indicatorId={indicatorId}
                indicator={historyData[indicatorId]}
                onClick={() => onIndicatorClick?.(indicatorId)}
              />
            ))}
        </Box>
      )}
      
      {!loadingHistory && Object.keys(historyData).length === 0 && (
        <Alert severity="warning">
          {t('observations.noHistoryData', 'No historical data available.')}
        </Alert>
      )}
    </Box>
  );
}

export default IndicatorChartsView;
